import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect acquisition source from URL params (runs once, before render decisions)
const detectUrlAttribution = () => {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  const source = p.get("source") || p.get("utm_source");
  if (!source) return null;
  const detailParts = [];
  if (p.get("utm_medium")) detailParts.push(`medium=${p.get("utm_medium")}`);
  if (p.get("utm_campaign")) detailParts.push(`campaign=${p.get("utm_campaign")}`);
  return { source: source.trim(), source_detail: detailParts.join(" | ") };
};

export const WaitlistForm = () => {
  const { t } = useLang();
  const f = t.form;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "", company: "", source: "", source_detail: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [error, setError] = useState("");
  const [urlAttributed, setUrlAttributed] = useState(false);

  useEffect(() => {
    const attr = detectUrlAttribution();
    if (attr) {
      setUrlAttributed(true);
      setForm((prev) => ({ ...prev, source: attr.source, source_detail: attr.source_detail }));
    }
  }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const detailPrompt = f.sourceDetailPrompts?.[form.source];

  const validate = () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.role) {
      setError(f.errorRequired);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(f.errorEmail);
      return false;
    }
    setError("");
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/registrations`, form);
      setStatus("success");
    } catch (err) {
      setError(f.errorGeneric);
      setStatus("idle");
    }
  };

  const reset = () => {
    setForm({ first_name: "", last_name: "", email: "", role: "", company: "", source: urlAttributed ? form.source : "", source_detail: urlAttributed ? form.source_detail : "" });
    setStatus("idle");
    setError("");
  };

  return (
    <div className="relative min-h-[520px]" data-testid="waitlist-form-wrap">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="waitlist-success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-start justify-center py-16"
          >
            <div className="flex h-14 w-14 items-center justify-center border border-[#D4AF37]">
              <Check className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h3 className="mt-8 font-serif-display text-4xl md:text-5xl text-[#FAFAFA]">{f.successTitle}</h3>
            <p className="mt-4 max-w-md font-sans-body text-base text-zinc-400 leading-relaxed">{f.successBody}</p>
            <button
              data-testid="register-again-btn"
              onClick={reset}
              className="mt-10 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-[#D4AF37] transition-colors duration-300"
            >
              {f.again}
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="waitlist-form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="space-y-10 py-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{f.firstName}</label>
                <input
                  data-testid="input-first-name"
                  value={form.first_name}
                  onChange={update("first_name")}
                  className="flush-input w-full py-3 text-lg"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{f.lastName}</label>
                <input
                  data-testid="input-last-name"
                  value={form.last_name}
                  onChange={update("last_name")}
                  className="flush-input w-full py-3 text-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{f.email}</label>
              <input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="flush-input w-full py-3 text-lg"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{f.role}</label>
                <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger
                    data-testid="select-role"
                    className="w-full rounded-none border-0 border-b border-white/10 bg-transparent px-0 py-3 text-lg text-[#FAFAFA] focus:ring-0 focus:border-[#D4AF37] data-[placeholder]:text-zinc-600"
                  >
                    <SelectValue placeholder={f.rolePlaceholder} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-white/10 bg-[#141414] text-[#FAFAFA]">
                    {f.roles.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`role-option-${r}`} className="rounded-none focus:bg-white/5 focus:text-[#D4AF37]">
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  {f.company} <span className="text-zinc-700 lowercase tracking-normal">({f.companyOptional})</span>
                </label>
                <input
                  data-testid="input-company"
                  value={form.company}
                  onChange={update("company")}
                  className="flush-input w-full py-3 text-lg"
                />
              </div>
            </div>

            {!urlAttributed && (
              <div data-testid="source-field">
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  {f.sourceLabel} <span className="text-zinc-700 lowercase tracking-normal">({f.sourceOptional})</span>
                </label>
                <Select value={form.source} onValueChange={(v) => setForm((p) => ({ ...p, source: v, source_detail: "" }))}>
                  <SelectTrigger
                    data-testid="select-source"
                    className="w-full rounded-none border-0 border-b border-white/10 bg-transparent px-0 py-3 text-lg text-[#FAFAFA] focus:ring-0 focus:border-[#D4AF37] data-[placeholder]:text-zinc-600"
                  >
                    <SelectValue placeholder={f.sourcePlaceholder} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-white/10 bg-[#141414] text-[#FAFAFA]">
                    {f.sourceOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`source-option-${o.value}`} className="rounded-none focus:bg-white/5 focus:text-[#D4AF37]">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {detailPrompt && (
                  <div className="mt-8">
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{detailPrompt}</label>
                    <input
                      data-testid="input-source-detail"
                      value={form.source_detail}
                      onChange={update("source_detail")}
                      className="flush-input w-full py-3 text-lg"
                    />
                  </div>
                )}
              </div>
            )}

            {error && (
              <p data-testid="form-error" className="text-sm text-red-400 font-sans-body">{error}</p>
            )}

            <button
              type="submit"
              data-testid="submit-waitlist-btn"
              disabled={status === "loading"}
              className="group relative w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] px-12 py-5 text-sm uppercase tracking-[0.2em] font-medium text-black transition-colors duration-300 hover:bg-[#F3E5AB] disabled:opacity-70"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {f.submitting}
                </>
              ) : (
                <>
                  {f.submit}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
